import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { EnableClassTelemetry } from 'app/services/otel.service';
import { StorageAPIService, StorageListResult } from '../storage-api.service';
import { SystemService } from '../../services/system.service';
import { urlFor } from './utils';

export interface Annotation<T> {
  id?: string;
  createdAt?: number;
  userId?: string;
  comment?: string;
  docId?: string;
  data: T;
}

export interface SentimentAnnotationData {
  sentimentModify: {
    sentimentScore: number;
  };
}

export interface EntityAddAnnotationData {
  entityAdd: {
    title: string;
    label?: string;
    type?: string;
    entityType?: string;
    sentimentScore?: number;
    isAbout?: boolean;
  };
}

export interface EntityEditAnnotationData {
  entityModify: {
    entityId: string;
    title: string;
    label?: string;
    type?: string;
    entityType?: string;
    sentimentScore?: number;
    isAbout?: boolean;
  };
}

export interface EntityDeleteAnnotationData {
  entityRemove: {
    entityId: string;
  };
}

export interface TopicAddAnnotationData {
  topicAdd: {
    title: string;
    type?: string;
    sentimentScore?: number;
  };
}

export interface TopicEditAnnotationData {
  topicModify: {
    topicId: string;
    title: string;
    type?: string;
    sentimentScore?: number;
  };
}

export interface TopicDeleteAnnotationData {
  topicRemove: {
    topicId: string;
  };
}

export enum SentimentEnum {
  POSITIVE = 'positive',
  NEUTRAL = 'neutral',
  NEGATIVE = 'negative',
  ALL = 'all',
}

@EnableClassTelemetry()
@Injectable()
export class AnnotationService extends StorageAPIService {
  constructor(
    public http: HttpClient,
    public systemService: SystemService
  ) {
    super(http, systemService);
  }

  private getUrl(projectId, collectionName, documentId) {
    return urlFor(
      `/collections/${encodeURIComponent(collectionName)}/documents/${encodeURIComponent(
        documentId
      )}/annotations`,
      projectId
    );
  }

  getDocumentSentiment(annotations: Annotation<any>[]): Annotation<SentimentAnnotationData> {
    return annotations
      ?.filter((an) => an.data.sentimentModify)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  }

  getDocumentSentiments(annotations: Annotation<SentimentAnnotationData>[], docs: any[]) {
    const docSentimentAnnotations = annotations.filter((a) =>
      docs.some((t) => a.data.sentimentModify && a.docId === t.id)
    );

    const latestItemEdits = this.getLatestItemEdits(
      docSentimentAnnotations,
      (annotation) => annotation.docId
    );

    // Update the items array with the latest modification
    for (const [itemId, annotation] of latestItemEdits) {
      const idx = docs.findIndex((t) => t.id === itemId);
      docs[idx].analyses[0].sentimentScore = annotation.data.sentimentModify.sentimentScore;
    }

    return docs;
  }

  getItems(
    type: 'topic' | 'entity',
    annotations: Annotation<TopicEditAnnotationData | EntityEditAnnotationData>[],
    items: any[]
  ) {
    const modifyProp = `${type}Modify`;
    const idTypeProp = `${type}Id`;
    const modifiedItems = this.getAddAndRemoveItems(type, items, annotations, idTypeProp);

    const itemTypeAnnotations = annotations.filter((a) =>
      modifiedItems.some((t) => t.id === a.data[modifyProp]?.[idTypeProp])
    );

    const latestItemEdits = this.getLatestItemEdits(
      itemTypeAnnotations,
      (annotation) => annotation.data[modifyProp][idTypeProp]
    );

    // Update the items array with the latest modification
    for (const [itemId, annotation] of latestItemEdits) {
      const idx = modifiedItems.findIndex((t) => t.id === itemId);
      modifiedItems[idx] = { ...modifiedItems[idx], ...annotation.data[modifyProp] };
    }

    return modifiedItems;
  }

  private getAddAndRemoveItems(
    type: 'topic' | 'entity',
    items: any[],
    annotations: Annotation<TopicEditAnnotationData | EntityEditAnnotationData>[],
    idTypeProp: string
  ) {
    const addProp = `${type}Add`;
    const removeProp = `${type}Remove`;
    const getAnnotations = <A>(operation: string): Annotation<A>[] => {
      return annotations.filter((a) => a.data[operation]) as Annotation<A>[];
    };

    const removedIds = getAnnotations<TopicDeleteAnnotationData | EntityDeleteAnnotationData>(
      removeProp
    ).map((a) => a.data[removeProp][idTypeProp]);
    const results = items.filter((e) => !removedIds.includes(e.id));

    const addedAnnotationItems = getAnnotations<TopicAddAnnotationData | EntityAddAnnotationData>(
      addProp
    ).filter((e) => !removedIds.includes(e.id));

    results.push(
      ...addedAnnotationItems.map((a) => ({
        id: a.id,
        isAnnotation: true,
        title: a.data[addProp].title,
        type: a.data[addProp].type,
        sentimentScore: a.data[addProp].sentimentScore,
        entityType: a.data[addProp].entityType,
      }))
    );
    return results;
  }

  //Create map of latest item annotations
  private getLatestItemEdits<T>(
    annotations: Annotation<T>[],
    getItemId: (annotation: Annotation<T>) => string
  ): Map<string, Annotation<T>> {
    return annotations.reduce((acc, annotation) => {
      const itemId = getItemId(annotation);

      const currentDate = new Date(annotation.createdAt);
      const storedDate = acc.get(itemId)?.createdAt
        ? new Date(acc.get(itemId)!.createdAt)
        : undefined;

      if (!storedDate || currentDate > storedDate) {
        acc.set(itemId, annotation);
      }
      return acc;
    }, new Map<string, Annotation<T>>());
  }

  async setDocumentSentiment(
    projectId,
    collectionName,
    documentId,
    score: number,
    comment?: string
  ): Promise<any> {
    const body: Annotation<SentimentAnnotationData> = {
      comment,
      data: {
        sentimentModify: {
          sentimentScore: score,
        },
      },
    };

    return await this.post(this.getUrl(projectId, collectionName, documentId), { ...body });
  }

  async addEntity(projectId, collectionName, documentId, comment, entity): Promise<any> {
    const body: Annotation<EntityAddAnnotationData> = {
      comment,
      data: {
        entityAdd: {
          title: entity.title,
          type: 'annotation',
          entityType: entity.entityType,
          sentimentScore: entity.sentimentScore,
        },
      },
    };

    return await this.post(this.getUrl(projectId, collectionName, documentId), { ...body });
  }

  async editEntity(projectId, collectionName, documentId, comment, entity): Promise<any> {
    const body: Annotation<EntityEditAnnotationData> = {
      comment,
      data: {
        entityModify: {
          entityId: entity.id,
          title: entity.title,
          entityType: entity.entityType,
          sentimentScore: entity.sentimentScore,
        },
      },
    };

    return await this.post(this.getUrl(projectId, collectionName, documentId), { ...body });
  }

  async deleteEntity(projectId, collectionName, documentId, comment, entityId): Promise<any> {
    const body: Annotation<EntityDeleteAnnotationData> = {
      comment,
      data: {
        entityRemove: {
          entityId,
        },
      },
    };

    return await this.post(this.getUrl(projectId, collectionName, documentId), { ...body });
  }

  async addTopic(projectId, collectionName, documentId, comment, topic): Promise<any> {
    const body: Annotation<TopicAddAnnotationData> = {
      comment,
      data: {
        topicAdd: {
          title: topic.title,
          type: topic.type,
          sentimentScore: topic.sentimentScore,
        },
      },
    };

    return await this.post(this.getUrl(projectId, collectionName, documentId), { ...body });
  }

  async editTopic(projectId, collectionName, documentId, comment, topic): Promise<any> {
    const body: Annotation<TopicEditAnnotationData> = {
      comment,
      data: {
        topicModify: {
          topicId: topic.id,
          title: topic.title,
          type: topic.type,
          sentimentScore: topic.sentimentScore,
        },
      },
    };

    return await this.post(this.getUrl(projectId, collectionName, documentId), { ...body });
  }

  async deleteTopic(projectId, collectionName, documentId, comment, topicId): Promise<any> {
    const body: Annotation<TopicDeleteAnnotationData> = {
      comment,
      data: {
        topicRemove: {
          topicId,
        },
      },
    };

    return await this.post(this.getUrl(projectId, collectionName, documentId), { ...body });
  }

  async getAnnotations(
    projectId: string,
    collectionName: string,
    limit = 1000
  ): Promise<StorageListResult<Annotation<any>>> {
    const options = { 'page-limit': limit };
    return await this.getPage(
      urlFor(`/collections/${encodeURIComponent(collectionName)}/annotations`, projectId),
      options as any
    );
  }
}
